import React from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, MapPinIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { ServiceAreaAccordion } from '../components/ServiceAreaAccordion';
import { CTABand } from '../components/CTABand';
import { counties, featuredCities, countyCities } from '../data/serviceAreas';
import { site, images } from '../data/site';
export function ServiceArea() {
  const totalCities = countyCities.reduce((sum, c) => sum + c.cities.length, 0);
  return <>
      <PageHero eyebrow="Service area" title="Where we work in Michigan" description={`${counties.join(', ')} Counties, Michigan — ${totalCities} communities across southeast Michigan.`} crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Service Area'
    }]} image={images.home} data-id="element-445" data-mp="3003wbhms6ek7" />

      <section className="border-b border-steel-200 bg-white" data-id="element-446" data-mp="2l4e8ddy1brcu">
        <div className="mx-auto grid max-w-7xl gap-4 px-6 py-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-447" data-mp="17l2zaon7fh7e">
          {[{
          value: String(counties.length),
          label: 'Counties served'
        }, {
          value: String(totalCities),
          label: 'Cities and townships'
        }, {
          value: site.estimatePhone.display,
          label: 'Estimates & plumbing',
          href: site.estimatePhone.href
        }, {
          value: `${site.address.city}, ${site.address.state}`,
          label: 'Based in'
        }].map(stat => <div key={stat.label} className="border-l-4 border-safety bg-bone px-5 py-4" data-id="element-448" data-mp="1gd4wme1z8hdg">
              {stat.href ? <a href={stat.href} className="font-display text-2xl font-bold tracking-tight text-ink hover:text-safety" data-id="element-449" data-mp="1lamxdh5ubqad">
                  {stat.value}
                </a> : <p className="font-display text-3xl font-bold tracking-tight text-ink" data-id="element-450" data-mp="2cjgd8e6yol4a">
                  {stat.value}
                </p>}
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-steel-400" data-id="element-451" data-mp="27l52804xk4kh">
                {stat.label}
              </p>
            </div>)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 md:py-16" data-id="element-452" data-mp="1xuacumwe0out">
        <SectionHeading eyebrow="Frequently served" title="Cities we are called to most" description="These communities show up most often on our schedule, but we cover every city listed below." data-id="element-453" data-mp="1svz3sedx2idn" />
        <div className="mt-6 flex flex-wrap gap-2" data-id="element-454" data-mp="1jvas3qbzwu3x">
          {featuredCities.map(city => <span key={city} className="inline-flex items-center gap-1.5 rounded-sm border border-steel-200 bg-white px-3 py-2 text-sm font-medium text-ink-700" data-id="element-455" data-mp="1exsevnqy3sq4">
              <MapPinIcon className="h-3.5 w-3.5 text-safety" aria-hidden="true" data-id="element-456" data-mp="39aryrt2cz937" />
              {city}
            </span>)}
        </div>

        <div className="mt-12" data-id="element-457" data-mp="3ls2r878aj73q">
          <SectionHeading eyebrow="Full coverage" title="County by county" description="Open a county to see every city and township we cover, including for plumbing service." data-id="element-458" data-mp="3lr9s9sfkplw5" />
          <div className="mt-6" data-id="element-459" data-mp="3vkgvp7qr6rtl">
            <ServiceAreaAccordion data-id="element-460" data-mp="2xuvsn0owpaf2" />
          </div>
        </div>
      </section>

      <section className="border-t border-steel-200 bg-white" data-id="element-461" data-mp="32tqiunp7e197">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 lg:grid-cols-2 lg:items-center" data-id="element-462" data-mp="32u8m44aa9fqm">
          <div data-id="element-463" data-mp="37r8jlqt2ha64">
            <h2 className="font-display text-3xl font-bold uppercase leading-tight tracking-tight text-ink" data-id="element-464" data-mp="37uwccnqotsjs">
              Plumbing service across all four counties
            </h2>
            <p className="mt-3 text-base leading-relaxed text-steel-500" data-id="element-465" data-mp="n22mate3qaqx">
              Emergency plumbing service, sewer and drain cleaning, frozen and
              burst pipes, flooding basements, water heaters and full plumbing
              inspections are available throughout the county and city lists
              above.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-466" data-mp="zkwxd4t5wvwe">
              <a href={site.plumbingPhone.href} className="flex items-center justify-center gap-2 rounded-sm bg-safety px-6 py-4 text-lg font-bold tracking-wide text-white transition hover:bg-safety-600" data-id="element-467" data-mp="zj3nlgn95vk6">
                <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-468" data-mp="14l2qyqizssh4" />
                {site.plumbingPhone.display}
              </a>
              <Link to="/services/plumbing" className="flex items-center justify-center gap-2 rounded-sm border-2 border-ink px-6 py-4 text-base font-bold uppercase tracking-wide text-ink transition hover:bg-ink hover:text-white" data-id="element-469" data-mp="1ib6b9bybac3x">
                Plumbing services
              </Link>
            </div>
          </div>
          <img src={images.plumbing} alt="Plumbing service work" className="h-64 w-full rounded-sm border border-steel-200 object-cover lg:h-80" data-id="element-470" data-mp="26waxz5ogzeej" />
        </div>
      </section>

      <CTABand title="Not sure if you are in our area?" description="Call the estimate line and tell us your city — we will let you know." data-id="element-471" data-mp="1wxlg3cwkptya" />
    </>;
}